<script setup lang="ts">
import { RouterView } from 'vue-router';
import { DrawerService } from '@/core/shared/drawer/drawer.service';
import { useDisplay } from 'vuetify';
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import ProjectService from '@/core/shared/project/project.service';
import ProjectCreateJobDialog from '@/components/Shared/Projects/ProjectCreateJobDialog.vue';
import { DialogService } from '@/core/shared/dialog/dialog.service';
import ProjectsBar from '@/components/Shared/LayoutBar/ProjectsBar/ProjectsBar.vue';
import { useShowMobileView } from '@/composables/useShowMobileView';

const display = useDisplay();
const drawerService = new DrawerService();
const dialogService = new DialogService();
const projectService = new ProjectService();

const hasStartedProjectListInfiniteScroll = ref<boolean>(false);
const isDesktop = computed(() => display.mdAndUp.value);

// On a phone the mobile experience owns the screen, so the projects drawer (the
// desktop side panel) must not render over it. Always present on desktop.
const showMobileView = useShowMobileView();

const hasProjects = computed(() => {
  return projectService.projects.length > 0;
});

const isSearchingProjects = computed(() => {
  return projectService.isSearchingProjects;
});

function startProjectListInfiniteScrollObserver(): boolean {
  const loadMoreElement = document.querySelector('#project-infinite-scroll');
  if (!loadMoreElement) {
    return false;
  }

  const infiniteScroll = new IntersectionObserver((entries) => {
    if (entries[0].isIntersecting) {
      projectService.searchMoreProjects();
    }
  });

  infiniteScroll.observe(loadMoreElement);
  return true;
}

async function handleProjectListInfiniteScrollObserver(isSearchingProjects: boolean) {
  if (isSearchingProjects || !hasProjects.value || hasStartedProjectListInfiniteScroll.value) {
    return;
  }

  if (projectService.isProjectFromDeepLinkRemoved) {
    return;
  }

  // Default watcher flush runs before DOM updates; wait a tick so the sentinel
  // rendered by ProjectList is actually queryable on the first attempt.
  await nextTick();

  if (startProjectListInfiniteScrollObserver()) {
    hasStartedProjectListInfiniteScroll.value = true;
  }
}

function scrollSelectedProjectIntoView() {
  const currentProjectId = projectService.currentProject?.id;

  if (!currentProjectId) {
    return;
  }

  const selectedProjectElement = document.getElementById(`project-card-${currentProjectId}`);

  selectedProjectElement?.scrollIntoView({
    behavior: 'instant',
    block: 'center',
    inline: 'nearest',
  });
}

watch(isSearchingProjects, (value) => {
  handleProjectListInfiniteScrollObserver(value);
});

// Ensure the attach flag gets reset whenever the list empties, even if
// isSearchingProjects does not transition (e.g. a filter change that clears
// projects and unmounts ProjectList). Without this, a later reload would keep
// the flag set and skip re-attaching to the newly rendered sentinel.
watch(hasProjects, (value) => {
  if (!value) {
    hasStartedProjectListInfiniteScroll.value = false;
  }
});

onMounted(scrollSelectedProjectIntoView);
</script>

<template>
  <div :class="{ 'h-full': showMobileView }">
    <!-- Full height in the mobile experience so the RouterView (jobs/index.vue ->
         MobileConversationsView) can fill the viewport and keep the composer pinned to
         the bottom; desktop keeps auto height. -->
    <v-navigation-drawer
      v-if="!showMobileView"
      width="336"
      v-model:model-value="drawerService.isProjectsDrawerOpen"
      color="shade-860"
      :permanent="isDesktop"
    >
      <ProjectsBar />
    </v-navigation-drawer>
    <ProjectCreateJobDialog v-model="dialogService.isCreateProjectDialogOpen" />
    <!-- 
      Suspense prevents race conditions during deep link navigation by ensuring
      employer data is loaded before child components render. Without this,
      components would need error-prone polling to check if data is ready.
      See: https://vuejs.org/guide/built-ins/suspense.html
    -->
    <Suspense>
      <RouterView />
      <template #fallback>
        <div class="flex h-full items-center justify-center">
          <v-progress-circular indeterminate color="primary" />
        </div>
      </template>
    </Suspense>
  </div>
</template>

<style scoped></style>
